import { addMinutes, parse } from 'date-fns'

import { isHairdressingService } from '@/shared/types/service-checkers'
import {
    HairSalonReservation,
    HairSalonScheduledTask,
    HairSalonTask,
    Pet,
    Reservation,
    UnscheduledHairSalonTask,
} from '@monorepo/functions/src/types/reservations'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'

export function getPetFromReservation(reservation: Reservation, petIndex: number): Pet | undefined {
    if (reservation.type === 'hotel') {
        return reservation.pets[petIndex]
    }
    if (reservation.pet) {
        return reservation.pet
    }
    return reservation.client.pets?.[petIndex] ?? reservation.client.pets?.[0]
}

export const DEFAULT_DURATIONS: Record<HairdressingServiceType, number> = {
    bath_and_brush: 60,
    bath_and_trim: 90,
    stripping: 120,
    deshedding: 90,
    brushing: 45,
    spa: 45,
    spa_ozone: 60,
    knots: 30,
    nail_trimming: 90,
}

// Helper to generate unique IDs
export function generateId(): string {
    return Math.random().toString(36).substr(2, 9)
}

// Helper to add minutes to a time string (HH:mm)
export function addMinutesToTimeString(time: string, minutes: number): string {
    const [hours, mins] = time.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, mins)
    const newDate = addMinutes(date, minutes)
    return `${String(newDate.getHours()).padStart(2, '0')}:${String(newDate.getMinutes()).padStart(2, '0')}`
}

export function createTasksFromReservation(
    reservation: HairSalonReservation,
    initialDate: string,
    initialTime: string,
): HairSalonScheduledTask[] {
    const tasks: HairSalonScheduledTask[] = []
    let currentTime = initialTime

    reservation.additionalServices.filter(isHairdressingService).forEach(service => {
        service.services.forEach(serviceType => {
            tasks.push({
                id: generateId(),
                reservationId: reservation.id,
                service: {
                    type: 'hairdressing',
                    petIndex: service.petIndex,
                    services: [serviceType],
                },
                date: initialDate,
                time: currentTime,
                duration: DEFAULT_DURATIONS[serviceType],
            })

            // Calculate next task start time
            currentTime = addMinutesToTimeString(currentTime, DEFAULT_DURATIONS[serviceType])
        })
    })

    return tasks
}

export function calculateTaskConflicts(
    tasks: HairSalonScheduledTask[],
    newTask: HairSalonScheduledTask,
): HairSalonScheduledTask[] {
    const conflicts: HairSalonScheduledTask[] = []
    const newTaskStart = parse(`${newTask.date} ${newTask.time}`, 'yyyy-MM-dd HH:mm', new Date())
    const newTaskEnd = addMinutes(newTaskStart, newTask.duration)

    tasks.forEach(task => {
        if (task.id === newTask.id) return // Skip the task itself

        if (task.date === newTask.date) {
            const taskStart = parse(`${task.date} ${task.time}`, 'yyyy-MM-dd HH:mm', new Date())
            const taskEnd = addMinutes(taskStart, task.duration)

            // Check if tasks overlap
            if (
                (newTaskStart >= taskStart && newTaskStart < taskEnd) ||
                (newTaskEnd > taskStart && newTaskEnd <= taskEnd) ||
                (newTaskStart <= taskStart && newTaskEnd >= taskEnd)
            ) {
                conflicts.push(task)
            }
        }
    })

    return conflicts
}

export function getTaskEndTime(task: HairSalonScheduledTask): string {
    return addMinutesToTimeString(task.time, task.duration)
}

export function isTaskInProgress(task: HairSalonScheduledTask): boolean {
    const now = new Date()
    const taskDate = parse(`${task.date} ${task.time}`, 'yyyy-MM-dd HH:mm', new Date())
    const taskEnd = addMinutes(taskDate, task.duration)

    return now >= taskDate && now < taskEnd
}

export function isTaskUnscheduled(task: HairSalonTask): task is UnscheduledHairSalonTask {
    return !('time' in task)
}

export function areSameUnscheduledTask(task1: UnscheduledHairSalonTask, task2: UnscheduledHairSalonTask): boolean {
    return task1.reservation.id === task2.reservation.id && task1.petIndex === task2.petIndex && task1.service === task2.service
}
// TODO REVIEW USAGES TO SEE IF WE NEED TO SEND CONFIRMATION EMAIL ------------------------------------------------------------
export function findUnscheduledTasks(reservations: Reservation[]): UnscheduledHairSalonTask[] {
    return reservations.flatMap(reservation => {
        const hairdressingServices = reservation.additionalServices.filter(isHairdressingService)
        const scheduled = reservation.tasks || []
        return hairdressingServices.flatMap(service =>
            service.services
                .filter(
                    serviceType =>
                        !scheduled.some(t => t.service === serviceType && t.petIndex === service.petIndex),
                )
                .map(serviceType => {
                    const unscheduledService: UnscheduledHairSalonTask = {
                        reservation,
                        service: serviceType,
                        petIndex: service.petIndex,
                    }
                    return unscheduledService
                }),
        )
    })
}